import {observer} from "mobx-react";
import { useMemo } from "react";
import './rma-page-data-cards.scss';
import Chip, {CHIP_COLORS} from "../../../../../components/chip/chip";
import DateTimeV2, {DATETIME_VIEW} from "../../../../../components/datetime-v2/datetime-v2";
import FontBody1 from "../../../../../components/fonts/font-body1/font-body1";
import FontBody3 from "../../../../../components/fonts/font-body3/font-body3";
import {FONT_COLOR, FONT_VARIANT} from "../../../../../components/fonts/font-props";
import DataCard from "../../../../../components/layouts/data-card/data-card";
import DataCardRmaOutOfPolicyIndicator from "../../../../../components/layouts/data-card/data-card-indicators/data-card-rma-out-of-policy-indicator";
import DataCardGridLayout from "../../../../../components/layouts/data-card/data-card-layouts/data-card-grid-layout";
import LinkableList from "../../../../../components/linkable-list/linkable-list";
import CustomerDataCard from "../../../../create-rma/components/customer-data-card";
import currentAccountStore from "../../../../accounts/stores/current-account-store";
import RmaShopperDetailsStore from "../../stores/rma-shopper-details-store";
import ExternalLink from "../../../components/external-link/external-link";
import RmaStatusChip from "../../../components/rma-status-chip/rma-status-chip";
import RmaPageStore from "../../stores/rma-page-store";
import RmaRequireAttentionIcon from "../rma-require-attention-icon/rma-require-attention-icon";
import IconGift from "../../../../../components/icons/icon-gift/icon-gift";
import IconRefunded from "../../../../../components/icons/icon-refunded/icon-refunded";
import IconCredited from "../../../../../components/icons/icon-credited/icon-credited";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import RmasLinkableList from "../../../../create-rma/components/rmas-linkable-list";
import IconPlatform from "../../../../../components/icons/icon-platform/icon-platform";
import { useDateTimeFormattingParams } from "../../../../../hooks/use-datetime-formatting-params";

interface IProps{
	store: RmaPageStore;
}
const RmaPageDataCards:React.FC<IProps> = observer(({store})=>{
	const customerCaseStore = store.customerCaseStore;
	const customerStore = customerCaseStore?.customer;
	const orderStore = store.orderStore;
    const shopperDetails = useMemo(
        () => (currentAccountStore.isSysAdmin ? new RmaShopperDetailsStore(store.rmaId) : undefined),
        [store.rmaId, currentAccountStore.isSysAdmin],
    );
    const daysSinceFulfillment = orderStore?.getDaysSinceFulfillment() ?? null;
    const rmaUnresolvedError = store.errorListStore?.lastUnresolvedShortError;
    const validationData = store.validationStatusChipData();

    const { getTranslation } = useDashboardTranslation();
    const scope = 'dashboard-returns-rma-card';
    const dateTimeParams = useDateTimeFormattingParams();

    const hasAnyIndicator = (): boolean => {
        if(store.isOutOfPolicyRma()) {
            return true;
        }

        return false;
    }

    return (
		<div className={'rma-page-data-cards'}>
            <div className="rma-page-data-cards-wrapper">
                <DataCard
                    hasTopIndicator={hasAnyIndicator()}
                    TopIndicator={DataCardRmaOutOfPolicyIndicator}
                    toolbar={{
                        content: <div className="rma-page-data-card-toolbar">
                    <span className='data-card-rma-id-container'>
                        {!!rmaUnresolvedError && <RmaRequireAttentionIcon errorText={rmaUnresolvedError} />}
                        {customerCaseStore?.isGift && <IconGift />}
                        <FontBody1 color={FONT_COLOR.NEUTRAL10}>
                            RMA{store.rmaId}
                        </FontBody1>
                    </span>
                            {validationData   && <Chip color={validationData.validationChipColor} label={getTranslation(validationData.validationStatusTextTranslationKey, validationData.validationStatusTextTranslationScope)} /> }
                            {store.statusTranslationKey && <RmaStatusChip statusKey={store.statusTranslationKey} />}
                            {store.customerCaseStore?.usedCoverage && <Chip label={getTranslation('COVERED_BY_RETURN_GUARD', scope)} color={CHIP_COLORS.BLUE} />}
                        </div>
                    }}>
                    <DataCardGridLayout>
                        <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation("REQUEST_TIME", scope)}</FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                            {
                                store.createdAt ? <DateTimeV2 {...dateTimeParams} utcDateString={store.createdAt} dateTimeView={DATETIME_VIEW.FULL}/> : ''
                            }
                        </FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation("LANGUAGE", scope)} </FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>{customerCaseStore?.locale.toUpperCase()}</FontBody3>
                        {store.shouldShowGlobalERmaPayloadFields && (
                            <>
                                <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('GLOBAL_E_RETURN_ID', scope)}</FontBody3>
                                <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                    {!store.globalERmaPayload?.return_id
                                        ? getTranslation('PENDING', scope)
                                        : store.globalERmaPayload.return_id}
                                </FontBody3>
                            </>
                        )}
                    </DataCardGridLayout>
                </DataCard>
            </div>
            <div className="rma-page-data-cards-wrapper">
                <DataCard
                    hasTopIndicator={hasAnyIndicator()}
                    toolbar={{
                        content: <>
                        <div className="rma-page-data-card-toolbar">
                            <span className='data-card-rma-id-container'>
                                <IconPlatform orderStore={orderStore} exchangeTooltipText={getTranslation('EXCHANGE_ORDER_CREATED_BY_RETURNGO', scope)} />
                                <FontBody1 color={FONT_COLOR.NEUTRAL10}>{getTranslation("ORDER", scope)} </FontBody1>
                                {orderStore?.orderName && <ExternalLink label={orderStore.orderName} url={orderStore?.orderUrl} />}
                            </span>
                            {(customerCaseStore?.isRefunded || store.orderStore?.isRefunded) && <div className="rma-status-chip">
                                <Chip
                                    label={getTranslation("REFUNDED_STATUS", scope)}
                                    color={CHIP_COLORS.BLUE}
                                    icon={IconRefunded}
                                />
                            </div>}
                            {customerCaseStore?.isCredited && <div className="rma-status-chip"><Chip
                                label={getTranslation("CREDITED_STATUS", scope)}
                                color={CHIP_COLORS.BLUE}
                                icon={IconCredited}
                            />
                            </div>}
                            {orderStore?.isCancelled && <Chip
                                label={getTranslation("CANCELED_STATUS", scope)}
                                color={CHIP_COLORS.RED}
                            />}
                        </div>
                    </>
			}}>
				<DataCardGridLayout>
                    {!!orderStore && <>
                        <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('RMAS', 'dashboard-create-rma')} </FontBody3>
                        <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                        <RmasLinkableList
                                isLoading={orderStore.isLoadingRmas}
                                noData={getTranslation('DATA_VALUE_NOT_APPLICABLE', scope)}
                                rmasList={orderStore.rmasList}
                                searchInput={orderStore.orderId}
                            />
                    </FontBody3>
                    </>}
                    {customerCaseStore?.isCancelResolution()
                        ? <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('UNFULFILLED', scope)} </FontBody3>
                        : !!orderStore?.getFulfilledDate() && (
                            <>
                                <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('FULFILLED', scope)} </FontBody3>
                                <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                    <DateTimeV2 {...dateTimeParams} utcDateString={orderStore!.getFulfilledDate()!} dateTimeView={DATETIME_VIEW.DATE_ONLY}/>
                                    {daysSinceFulfillment !== null ? `(${daysSinceFulfillment} ${getTranslation('DAYS_AGO', scope)})` : ''}
                                </FontBody3>
                            </>
                        )
                    }
                    {!!customerCaseStore?.exchangeOrderList.length ?
                         <>
                            <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('RELATED_ORDERS', scope)}</FontBody3>
                            <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                <LinkableList list={
                                    customerCaseStore?.exchangeOrderList.slice(0, 3).map(i=>({
                                        label:i.orderName,
                                        href: i.orderLink ?? i.draftOrderLink,
                                    })) ?? []} totalCount={customerCaseStore?.exchangeOrderList.length} />
                            </FontBody3>
                        </>
                         :
                        null
                    }
                    {orderStore?.location &&
                        <>
                            <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('LOCATION', scope)} </FontBody3>
                            <FontBody3 color={FONT_COLOR.PRIMARY2}variant={FONT_VARIANT.BOLD}>{orderStore.location.name}</FontBody3>
                        </>
                    }
                    {store.shouldShowGlobalERmaPayloadFields && (
                        <>
                            <FontBody3 color={FONT_COLOR.PRIMARY2}>{getTranslation('GLOBAL_E_ORDER_ID', scope)}</FontBody3>
                            <FontBody3 color={FONT_COLOR.PRIMARY2} variant={FONT_VARIANT.BOLD}>
                                {store.globalERmaPayload?.order_id ?? ''}
                            </FontBody3>
                        </>
                    )}
				</DataCardGridLayout>
			</DataCard>
            </div>
                <div className="rma-page-data-cards-wrapper">
                    <CustomerDataCard
                        hasTopIndicator={hasAnyIndicator()}
                        customer={customerStore}
                        customerCase={customerCaseStore}
                        currencyCode={store.storeCurrency ?? ''}
                        orderShippingAddress={orderStore ? orderStore.shipmentCustomerAddress ?? 'N/A' : ''}
                        isGift={customerCaseStore?.isGift}
                        addressId={customerCaseStore?.addressId}
                        shopperDetails={shopperDetails}
                    />
                </div>
		</div>
	);
})

export default RmaPageDataCards;
